import { createSlice, PayloadAction, combineReducers } from '@reduxjs/toolkit'
import { LabOrder, Laboratory } from '@dataTypes/lab.types'
import {
  fetchLabOrders,
  addLabOrder,
  deleteLabOrder,
  updateLabOrder,
  fetchLaboratories,
  addLab,
  deleteLab,
  updateLab,
} from '@store/actions/lab.action'

interface LabOrderState {
  orders: LabOrder[]
  loading: boolean
  error: string | null
  initialized: boolean
}

const initialLabOrderState: LabOrderState = {
  orders: [],
  loading: false,
  error: null,
  initialized: false,
}

const labOrderSlice = createSlice({
  name: 'labOrders',
  initialState: initialLabOrderState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabOrders.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchLabOrders.fulfilled,
        (state, action: PayloadAction<LabOrder[]>) => {
          state.loading = false
          state.orders = action.payload
          state.initialized = true
        },
      )
      .addCase(fetchLabOrders.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(
        addLabOrder.fulfilled,
        (state, action: PayloadAction<LabOrder>) => {
          state.orders.push(action.payload)
          state.initialized = true
        },
      )
      .addCase(
        deleteLabOrder.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.orders = state.orders.filter(
            (order) => order.orderNo !== action.payload,
          )
        },
      )
      .addCase(
        updateLabOrder.fulfilled,
        (state, action: PayloadAction<LabOrder>) => {
          const index = state.orders.findIndex(
            (order) => order.orderNo === action.payload.orderNo,
          )
          if (index !== -1) {
            state.orders[index] = action.payload
          }
        },
      )
  },
})
interface LaboratoryState {
  labs: Laboratory[]
  loading: boolean
  error: string | null
  initialized: boolean
}

const initialLaboratoryState: LaboratoryState = {
  labs: [],
  loading: false,
  error: null,
  initialized: false,
}

const laboratorySlice = createSlice({
  name: 'laboratories',
  initialState: initialLaboratoryState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLaboratories.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchLaboratories.fulfilled,
        (state, action: PayloadAction<Laboratory[]>) => {
          state.labs = action.payload
          state.loading = false
          state.initialized = true
        },
      )
      .addCase(fetchLaboratories.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(addLab.fulfilled, (state, action: PayloadAction<Laboratory>) => {
        state.labs.push(action.payload)
      })
      .addCase(deleteLab.fulfilled, (state, action: PayloadAction<string>) => {
        state.labs = state.labs.filter((lab) => lab.id !== action.payload)
      })
      .addCase(
        updateLab.fulfilled,
        (state, action: PayloadAction<Laboratory>) => {
          const index = state.labs.findIndex(
            (lab) => lab.id === action.payload.id,
          )
          if (index !== -1) {
            state.labs[index] = action.payload
          }
        },
      )
  },
})

export default combineReducers({
  labOrders: labOrderSlice.reducer,
  laboratories: laboratorySlice.reducer,
})
